import { useState } from 'react';
import { Link } from 'react-router-dom';

/**
 * Public-facing Stripe Connect setup guide for clubs.
 * Linked from the Onboard page (pre-payment) and the Onboard Success page (post-payment).
 * Designed to be read alongside the Stripe Express onboarding tab.
 */
export default function StripeSetupGuide() {
  const [region, setRegion] = useState('IE'); // IE | UK

  return (
    <div className="min-h-screen bg-white">
      <header className="bg-white border-b border-[#1D3557]/5 px-6 py-4 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-3">
            <img
              src="https://www.targetfunder.com/tf_logo.png"
              alt="TargetFunder"
              className="object-contain w-auto"
              style={{ height: '56px' }}
            />
            <h1 className="font-heading font-black text-3xl sm:text-4xl uppercase tracking-tighter leading-[0.9] text-[#1D3557] m-0">
              target<span className="text-[#E63946]">funder</span>
              <span className="text-[#1D3557]/30 hidden sm:inline">.com</span>
            </h1>
          </Link>
          <div className="flex gap-1 p-1 bg-gray-100 rounded-lg text-xs font-bold" data-testid="region-toggle">
            <button
              onClick={() => setRegion('IE')}
              className={`px-3 py-1.5 rounded-md transition-colors flex items-center gap-2 ${region === 'IE' ? 'bg-white text-[#1D3557] shadow-sm' : 'text-gray-500'}`}
              data-testid="region-ie"
            ><img src="https://flagcdn.com/w40/ie.png" alt="" width="20" height="14" className="rounded-sm" />Ireland</button>
            <button
              onClick={() => setRegion('UK')}
              className={`px-3 py-1.5 rounded-md transition-colors flex items-center gap-2 ${region === 'UK' ? 'bg-white text-[#1D3557] shadow-sm' : 'text-gray-500'}`}
              data-testid="region-uk"
            ><img src="https://flagcdn.com/w40/gb.png" alt="" width="20" height="14" className="rounded-sm" />United Kingdom</button>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10">
        <p className="text-sm font-bold uppercase tracking-wider text-[#E63946]" data-testid="guide-eyebrow">Setup Guide</p>
        <h1 className="text-3xl sm:text-4xl font-black text-[#1D3557] mt-2 mb-3" data-testid="guide-headline">
          Stripe Connect — a sports club's plain-English walkthrough
        </h1>
        <p className="text-gray-600 mb-10">
          Stripe's onboarding is built for businesses. This guide translates every question into what a sports club should answer. It takes ~10 minutes.
          Keep this tab open while you're filling out Stripe in another.
        </p>

        {/* What you'll need */}
        <Section title="Before you start — what you'll need">
          <ul className="list-disc pl-6 space-y-2 text-gray-700 text-[15px]">
            <li>Photo ID of the person signing up — <strong>passport or driving licence</strong>. Stripe will ask you to upload an image of it and take a quick selfie on your phone camera for identity verification.</li>
            <li>Club's <strong>bank account details</strong> — {region === 'IE' ? 'IBAN and BIC' : 'sort code and account number'}</li>
            <li>Club's address as it appears on official correspondence</li>
            <li>A short description of what your club does (1–2 sentences is plenty — we suggest wording below)</li>
            <li>Name and email for <strong>one other committee member</strong> (Chairperson / Secretary / Treasurer — whoever)</li>
            <li><strong>A proof-of-club document</strong> — Stripe sometimes asks for this (see Step&nbsp;3). The easiest options are your <strong>club constitution</strong>, a <strong>recent bank statement</strong> with the club's name on it, a <strong>letter from your governing body</strong> (GAA / FAI / IRFU / LGFA / Camogie Association) confirming affiliation, or <strong>recent AGM minutes</strong>.</li>
          </ul>
        </Section>

        {/* Step-by-step */}
        <Section title="Step 1 · Pick your business type">
          <p className="text-gray-700 text-[15px] mb-3">
            Stripe will ask what type of business you are before the details page.
          </p>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm">
            <p className="font-bold text-[#1D3557] mb-2">Pick: <span className="text-[#E63946]">Non-profit</span> → then <span className="text-[#E63946]">Unincorporated non-profit</span></p>
            <p className="text-gray-700">
              This covers the vast majority of amateur sports clubs — committees running a shared bank account without a separate CRO / Companies House registration.
              Only pick <strong>Incorporated non-profit</strong> if your club is formally registered as a charity ({region === 'IE' ? 'CHY number' : 'Charity Commission number'}).
              Do not pick <strong>Individual</strong> — Stripe will force the bank account to match one named person.
            </p>
          </div>
        </Section>

        <Section title="Step 2 · Tell us about your business">
          <p className="text-gray-700 text-[15px] mb-3">
            Once you've picked <strong>Unincorporated non-profit</strong> in Step&nbsp;1, this page is short. Here's every field and what to put:
          </p>
          <Table
            rows={[
              ['Legal business name',
               'Your club\'s official name, exactly as it appears on your club bank account — e.g. "Walterstown GFC". Mismatches between this and the bank account name will fail verification.'],
              ['Business name (Trading name)',
               'Usually the same as your legal name — just retype it. Only different if your club trades publicly under a different name.'],
              ['Business address',
               <>Country: <strong>{region === 'IE' ? 'Ireland' : 'United Kingdom'}</strong>. Use the club's official postal address (the one on correspondence / letterhead). {region === 'IE' ? 'Eircode is required — look it up free at eircode.ie.' : 'Post code is required.'} It's fine if this is the Chairperson's or Secretary's home address so long as the club uses it.</>],
              ['Business phone number',
               <>A number Stripe can reach the club on. A committee member's mobile is fine — prefix <strong>{region === 'IE' ? '+353' : '+44'}</strong> and drop the leading 0 (so 086&nbsp;123&nbsp;4567 becomes 86&nbsp;123&nbsp;4567).</>],
              ['Industry',
               <>In the dropdown, expand <strong>"Membership organisations"</strong>, then pick <strong>"Charitable and Social Service Organizations"</strong> inside it. ⚠️ Do <strong>not</strong> pick Gambling, Lotteries, or Betting — predictor fundraisers are skill-based and Stripe will reject accounts flagged as gambling.</>],
              ['Business website',
               <>Your club website OR Facebook / Instagram page. Anything that proves the club exists publicly. ⚠️ Do not leave blank, and do not put a "coming soon" URL — Stripe will reject both.</>],
              ['Product description',
               <><strong>Copy-paste this and edit if you like:</strong><br/>
               <span className="block mt-2 p-2 bg-white border border-gray-200 rounded font-mono text-[13px] text-gray-700">
                 "Amateur sports club running short term fundraisers to support club finances."
               </span></>],
            ]}
          />
          <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-900">
            <strong>Why the Product description matters:</strong> Stripe's automated reviewer uses this field to decide whether to approve, hold for manual review, or reject. Mentioning "amateur sports club" and "fundraiser" up front helps them classify you correctly.
          </div>
        </Section>

        <Section title="Step 3 · Upload a document to verify your club">
          <p className="text-gray-700 text-[15px] mb-3">
            Around this point Stripe may show you an <strong>"Upload document"</strong> screen with a long list — VAT Registration, Certificate of Registration, Partnership Agreement, Certificate of Incorporation, Articles of Association, {region === 'IE' ? 'Companies Registration Office (CRO), ' : ''}and more. <strong>Amateur sports clubs almost never have any of these</strong>.
          </p>
          <p className="text-gray-700 text-[15px] mb-3">
            That's fine — scroll to the bottom of the list and pick <strong className="text-[#E63946]">"Other"</strong>, then upload any of the following:
          </p>
          <ul className="list-disc pl-6 space-y-2 text-gray-700 text-[15px] mb-4">
            <li><strong>Club constitution or rules document</strong> ← best choice; most clubs have one from their founding AGM.</li>
            <li><strong>A recent bank statement</strong> showing the club's name and address. Redact the balance and transaction amounts if you like — Stripe only needs to see the name/address header.</li>
            <li><strong>Affiliation letter from your governing body</strong> — {region === 'IE' ? <>GAA, FAI, IRFU, LGFA, Camogie Association, Athletics Ireland, Swim Ireland, etc.</> : <>FA, RFU, ECB, Scottish FA, Welsh Rugby Union, England Netball, etc.</>} confirming the club is registered with them.</li>
            <li><strong>Recent AGM minutes</strong> listing the current committee members.</li>
            <li><strong>A letter on club letterhead</strong> signed by two officers (e.g. Chair + Secretary) confirming the club name, address, and committee.</li>
          </ul>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-900">
            <strong>Not always asked:</strong> Stripe's automated system only requests this document for some applications — it depends on the product description, industry code, and other risk signals. If you don't see this screen, skip ahead. If you do, any of the documents above will satisfy it.
          </div>
        </Section>

        <Section title="Step 4 · Verify your personal details">
          <p className="text-gray-700 text-[15px] mb-3">
            Stripe is legally required to verify a real human behind every account. This is <strong>you personally</strong>, not the club — use your own details, not the club's.
          </p>
          <Table
            rows={[
              ['Legal name',
               <>Exactly as it appears on your passport or driving licence.</>],
              ['Email address',
               <>Your personal email is fine — Stripe uses this to contact the signer if there's a problem.</>],
              ['Job title',
               <>Whatever your committee role is — <strong>Chairperson, Treasurer, Secretary, PRO, Committee Member</strong>, whatever fits.</>],
              ['Date of birth',
               <>Yours, obviously. Must match ID.</>],
              ['Home address',
               <>⚠️ <strong>Your home address, NOT the club address.</strong> This is the single most common mistake on this page.</>],
              ['Phone number',
               <>Your personal mobile is fine. Prefix <strong>{region === 'IE' ? '+353' : '+44'}</strong> and drop the leading 0.</>],
              [<>Are you a citizen of {region === 'IE' ? 'Ireland' : 'the United Kingdom'}?</>,
               <>Pick <strong>Yes</strong> if you are. Stripe will then ask for your <strong>{region === 'IE' ? 'PPSN (Personal Public Service Number)' : 'National Insurance (NI) number'}</strong> — it's marked <em>Optional</em>, so it's your call whether to provide it. It's used only for KYC on you as the signer and is never displayed, shared with club members, or used for tax reporting on the club.</>],
              ['The two tick-boxes',
               <><strong>Leave</strong> <em>I own 25% or more of the company</em> unchecked — nobody owns a sports club.<br/>
               <strong>Tick</strong> <em>I'm a director of the company or member of the governing board</em> — that's the accurate one for any committee member.<br/>
               <span className="block mt-2 text-gray-700">When Stripe then asks <em>"Are you the only director / board member?"</em> — pick <strong>No</strong>. Your club has a full committee, you're just the person filling out this form.</span></>],
            ]}
          />
          <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-900">
            <strong>You'll upload ID later:</strong> Stripe doesn't ask for your passport / driving licence here — that happens on the review page at the end (Step&nbsp;9). Have your ID nearby before you start so you're not hunting for it mid-flow.
          </div>
        </Section>

        <Section title="Step 5 · Business owners">
          <p className="text-gray-700 text-[15px] mb-3">
            Stripe asks you to add anyone who owns 25% or more of the club.
          </p>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm">
            <p className="font-bold text-[#1D3557] mb-1">Click <span className="text-[#E63946]">"Continue with no owners"</span></p>
            <p className="text-gray-700">
              Nobody "owns" a club — it's run collectively by the committee. Don't add yourself or anyone else as an owner.
            </p>
          </div>
        </Section>

        <Section title="Step 6 · Business directors">
          <p className="text-gray-700 text-[15px] mb-3">
            Stripe will already have added you. You need to add at least one more.
          </p>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm mb-4">
            <p className="font-bold text-[#1D3557] mb-1">Add <span className="text-[#E63946]">one more committee member</span> — that's enough</p>
            <p className="text-gray-700">
              You don't need to list every committee member. One additional director (name + email) satisfies Stripe's requirement. Most clubs add the Chairperson or Secretary.
            </p>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-900">
            <strong>Heads up:</strong> the second director will show as <em>"Incomplete"</em> on the review page at the end. That's expected. When you get there, click <strong>Edit</strong> beside their name and fill in their <strong>date of birth</strong> and <strong>role in the club</strong> (e.g. Chairperson).
          </div>
        </Section>

        <Section title="Step 7 · Business executives">
          <p className="text-gray-700 text-[15px] mb-3">
            Stripe asks you to list any executives or senior managers.
          </p>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm">
            <p className="font-bold text-[#1D3557] mb-1">Click <span className="text-[#E63946]">"Continue with no executives"</span></p>
            <p className="text-gray-700">
              Amateur clubs don't have paid executives — your committee members already covered off the governance side in Step&nbsp;6. Don't add anyone here.
            </p>
          </div>
        </Section>

        <Section title="Step 8 · Bank account">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-900 mb-4">
            <strong>Important:</strong> use the <strong>club's bank account</strong>, not your personal one. Stripe matches the account holder name to the legal business name in Step 2.
          </div>
          {region === 'IE' ? (
            <p className="text-gray-700 text-[15px]">You'll enter an <strong>IBAN</strong> (IE29 AIBK ...) and <strong>BIC</strong>. Stripe may do a micro-deposit test — a €0.01 deposit that takes 1–2 days to clear. Once it lands, your account is verified.</p>
          ) : (
            <p className="text-gray-700 text-[15px]">You'll enter a UK <strong>sort code</strong> (6 digits) and <strong>account number</strong> (8 digits). Stripe verifies instantly for most UK banks.</p>
          )}
        </Section>

        <Section title="Step 9 · Review and submit">
          <p className="text-gray-700 text-[15px] mb-3">
            Stripe shows a one-page summary of everything you entered. Two things will show as <strong>"Incomplete"</strong> or <strong>"Invalid"</strong> on first look — both are expected and both have a 30-second fix.
          </p>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm mb-4">
            <p className="font-bold text-[#1D3557] mb-2">A · Your own name shows <span className="text-[#E63946]">"Invalid for management and ownership"</span></p>
            <p className="text-gray-700 mb-2">
              Click <strong>Edit</strong> beside your name. Stripe opens the <strong>identity verification</strong> flow — you'll upload a photo of your <strong>passport or driving licence</strong>, then take a quick <strong>selfie</strong> on your phone camera.
            </p>
            <p className="text-gray-700">
              Tip: Stripe usually gives you a link you can SMS to yourself to finish the ID + selfie step on your phone. If you're already on mobile, it just opens the camera directly.
            </p>
          </div>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm mb-4">
            <p className="font-bold text-[#1D3557] mb-2">B · The second director shows <span className="text-[#E63946]">"Incomplete"</span></p>
            <p className="text-gray-700">
              Click <strong>Edit</strong> beside their name and fill in their <strong>date of birth</strong> and <strong>role in the club</strong> (e.g. Chairperson). They don't need to upload any ID — only the signer (you) does.
            </p>
          </div>
          <ul className="list-disc pl-6 space-y-2 text-gray-700 text-[15px] mb-4">
            <li>Scroll everything else once — it should match what you entered.</li>
            <li>Tick the <strong>Connected Account Agreement</strong> and hit <strong>Agree and submit</strong>.</li>
          </ul>
          <p className="text-gray-700 text-[15px]">
            Once submitted, Stripe usually approves within a few minutes. You'll bounce straight back to TargetFunder — your predictor site goes live automatically and we'll email you a dashboard link.
          </p>
        </Section>

        {/* FAQ */}
        <Section title="Frequently asked">
          <FAQ q="Do I need to be a registered company or charity?" a="No. Stripe supports unincorporated associations, sports clubs and charities. If you're just a committee running a fundraiser, pick 'Non-profit → Unincorporated association' in Step 1." />
          <FAQ q="Stripe is asking me to upload a document to verify my club — I don't have any of the ones listed." a="Totally normal for amateur clubs. Scroll to the bottom of the list and pick 'Other', then upload any of: your club constitution, a recent bank statement showing the club's name and address, a letter from your governing body (GAA / FAI / IRFU / LGFA / Camogie Association etc.) confirming affiliation, or recent AGM minutes. Any one of these satisfies Stripe's proof-of-organisation check. See Step 3 for the full walkthrough." />
          <FAQ q="Why is Stripe asking for my PPSN / NI number? Do I have to give it?" a="It's marked Optional — it's your choice whether to provide it. If you do, Stripe uses it only to verify you as the signer (it's KYC on you personally, not on the club). It's never displayed, shared with your club members, or used for any tax reporting on the club's behalf." />
          <FAQ q="What happens to the money?" a={`Entries paid by your members go directly into your club's bank account — TargetFunder never holds the money. Stripe takes their usual processing fee (~1.5% + ${region === 'IE' ? '€0.25' : '£0.20'} per transaction). If you chose the per-entry plan, we also take £1 / €1 per entry (which already covers the Stripe fee). If you paid the one-off setup fee, you keep everything else.`} />
          <FAQ q="Why is Stripe asking for so much detail?" a="Because they're a regulated payment processor, they have to perform Know-Your-Customer checks on every account that receives money. It's the same level of checks any UK/IE business bank would run. Once done, you never have to do it again." />
          <FAQ q="I'm stuck on a step. What do I do?" a="Leave Stripe open in its tab, email support@targetfunder.com with a screenshot of the step you're on, and we'll reply within a couple of hours. You can always come back to Stripe later — your progress is saved." />
          <FAQ q="What if Stripe rejects my application?" a="Rare, but it happens. Stripe will tell you why (usually an address or ID mismatch). Contact support@targetfunder.com — if you paid the £195/€249 setup fee, we'll refund it in full when Stripe can't onboard you. (Per-entry clubs have nothing to refund — you only ever pay when entries land.)" />
          <FAQ q='Is this a "real" Stripe account?' a="Yes. We use Stripe Express — a streamlined flavour of Stripe that gets you set up in ~10 minutes instead of an hour. All the normal Stripe features (dashboard, payouts, refunds, dispute handling) work identically. You can log in at connect.stripe.com/express/login anytime." />
        </Section>

        <div className="mt-12 pt-6 border-t border-gray-200 text-sm text-gray-500">
          <p>Still stuck? Email <a className="text-[#1D3557] underline" href="mailto:support@targetfunder.com">support@targetfunder.com</a> — screenshots speed things up.</p>
        </div>
      </main>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mb-10" data-testid={`section-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
      <h2 className="text-xl font-black text-[#1D3557] mb-4 pb-2 border-b border-gray-200">{title}</h2>
      {children}
    </section>
  );
}

function Table({ rows }) {
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <table className="w-full text-sm">
        <tbody>
          {rows.map(([label, desc], i) => (
            <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
              <td className="py-2.5 px-4 font-semibold text-[#1D3557] align-top w-1/3">{label}</td>
              <td className="py-2.5 px-4 text-gray-700">{desc}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FAQ({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-gray-200 last:border-b-0">
      <button
        onClick={() => setOpen(!open)}
        className="w-full py-4 flex items-center justify-between text-left"
        data-testid={`faq-toggle-${q.toLowerCase().slice(0, 20).replace(/[^a-z0-9]+/g, '-')}`}
      >
        <span className="font-semibold text-[#1D3557] pr-4">{q}</span>
        <span className="text-gray-400 text-xl flex-shrink-0">{open ? '−' : '+'}</span>
      </button>
      {open && <p className="pb-4 text-gray-700 text-[15px] leading-relaxed">{a}</p>}
    </div>
  );
}
